package br.com.htms.inspeccaocampo.ui.dashboards

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.colaboradores.ColabDados
import br.com.htms.inspeccaocampo.data.colaboradores.Colaborador
import br.com.htms.inspeccaocampo.data.colaboradores.DatasColab
import br.com.htms.inspeccaocampo.data.colaboradores.DocMatriz
import br.com.htms.inspeccaocampo.data.colaboradores.DocsMatriz
import br.com.htms.inspeccaocampo.data.colaboradores.StatusColab
import br.com.htms.inspeccaocampo.data.colaboradores.StatusDoc
import br.com.htms.inspeccaocampo.ui.kit.BarrasPercentual
import br.com.htms.inspeccaocampo.ui.kit.CartaoSecao
import br.com.htms.inspeccaocampo.ui.kit.Celula
import br.com.htms.inspeccaocampo.ui.kit.CelulaCabecalho
import br.com.htms.inspeccaocampo.ui.kit.CelulaCabecalhoOrdenavel
import br.com.htms.inspeccaocampo.ui.kit.rememberOrdem
import br.com.htms.inspeccaocampo.ui.kit.ChipCor
import br.com.htms.inspeccaocampo.ui.kit.ChipFiltro
import br.com.htms.inspeccaocampo.ui.kit.CoresDash
import br.com.htms.inspeccaocampo.ui.kit.DashDatas
import br.com.htms.inspeccaocampo.ui.kit.FatiaDash
import br.com.htms.inspeccaocampo.ui.kit.FiltrosAtivos
import br.com.htms.inspeccaocampo.ui.kit.FmtDash
import br.com.htms.inspeccaocampo.ui.kit.GradeKpis
import br.com.htms.inspeccaocampo.ui.kit.GraficoColunas
import br.com.htms.inspeccaocampo.ui.kit.GraficoRosca
import br.com.htms.inspeccaocampo.ui.kit.KpiDash
import br.com.htms.inspeccaocampo.ui.kit.ItemPainel
import br.com.htms.inspeccaocampo.ui.kit.PaineisDoDash
import br.com.htms.inspeccaocampo.ui.kit.LinhaTabela
import br.com.htms.inspeccaocampo.ui.kit.PainelDashboard
import br.com.htms.inspeccaocampo.ui.kit.RankingBarras
import br.com.htms.inspeccaocampo.ui.kit.SemDadosDash
import br.com.htms.inspeccaocampo.ui.kit.SerieDash
import br.com.htms.inspeccaocampo.ui.kit.TabelaPortal
import br.com.htms.inspeccaocampo.ui.theme.PortalCores

private data class Vencimento(val colab: Colaborador, val doc: String, val data: String, val dias: Int, val status: StatusDoc)

@Composable
fun DashboardColaboradores(d: ColabDados, carregando: Boolean = false, onAtualizar: (() -> Unit)? = null) {
    var vinculo by remember { mutableStateOf("") }
    var fStatus by remember { mutableStateOf<String?>(null) }
    var fSetor by remember { mutableStateOf<String?>(null) }
    var fFuncao by remember { mutableStateOf<String?>(null) }
    var fObra by remember { mutableStateOf<String?>(null) }
    var fMes by remember { mutableStateOf<Int?>(null) }
    val hoje = remember { DatasColab.hoje() }
    val hojeIso = remember { DatasColab.isoDeDias(hoje) }
    val regras = d.regras

    fun setor(c: Colaborador) = c.setorGhe.ifBlank { "Sem setor/GHE" }
    fun funcao(c: Colaborador) = c.funcao.ifBlank { "Sem função" }
    fun obra(c: Colaborador) = d.centros.firstOrNull { it.id == c.ccId }?.rotulo?.ifBlank { null }
        ?: if (c.ccId.isBlank()) "Sem obra" else c.ccId

    val ativos = d.colaboradores.filter { it.noQuadro(hojeIso) && (vinculo.isBlank() || it.vinculo == vinculo) }
    val piorDe = remember(d, vinculo) { ativos.associate { it.id to StatusColab.pior(it, regras, hoje) } }
    val lista = ativos.filter { c ->
        (fStatus == null || piorDe[c.id]?.rotulo == fStatus) &&
            (fSetor == null || setor(c) == fSetor) &&
            (fFuncao == null || funcao(c) == fFuncao) &&
            (fObra == null || obra(c) == fObra)
    }
    val situacoes = remember(lista, regras) {
        lista.flatMap { c ->
            DocsMatriz.todos.map { doc -> Triple(c, doc, StatusColab.doc(c, doc.key, regras, hoje)) } +
                c.outros.map { extra -> Triple(c, DocMatriz(extra.id, extra.nome, "Documento personalizado"), StatusColab.outro(extra, regras, hoje)) }
        }
    }
    val emDia = situacoes.count { it.third.status.emDia }
    val vencidos = situacoes.count { it.third.status == StatusDoc.VENCIDO }
    val pendentes = situacoes.count { it.third.status == StatusDoc.PENDENTE }
    val criticos = situacoes.count { it.third.status == StatusDoc.CRITICO }
    val atencao = situacoes.count { it.third.status == StatusDoc.ATENCAO }
    val conformidade = (emDia + vencidos + pendentes).takeIf { it > 0 }?.let { emDia.toDouble() / it }
    val cnhs = lista.map { StatusColab.cnh(it, regras, hoje).status }
    val cnhVencidas = cnhs.count { it == StatusDoc.VENCIDO }

    val meses = remember(hojeIso) {
        (0 until 12).map { k ->
            var a = hojeIso.take(8) + "01"
            repeat(k) { a = DashDatas.add(a, 32).take(8) + "01" }
            val prox = DashDatas.add(a, 32).take(8) + "01"
            Triple(DashDatas.mesRotulo(a), if (k == 0) hojeIso else a, DashDatas.add(prox, -1))
        }
    }
    val vencimentos = situacoes.mapNotNull { (c, doc, s) ->
        if (s.vencimento.isBlank() || s.dias == null) null
        else Vencimento(c, doc.rotulo, s.vencimento, s.dias, s.status)
    } + lista.mapNotNull { c ->
        val s = StatusColab.cnh(c, regras, hoje)
        if (s.vencimento.isBlank() || s.dias == null) null else Vencimento(c, "CNH", s.vencimento, s.dias, s.status)
    }
    val futuros = vencimentos.filter { it.dias >= 0 }
    val rotulosMatriz = DocsMatriz.todos.map { it.rotulo }.toSet()
    fun mesDe(v: Vencimento) = meses.indexOfFirst { v.data >= it.second && v.data <= it.third }

    val filtrosAtivos = buildList {
        fStatus?.let { add("Situação: $it" to { fStatus = null }) }
        fSetor?.let { add("Setor: $it" to { fSetor = null }) }
        fFuncao?.let { add("Função: $it" to { fFuncao = null }) }
        fObra?.let { add("Obra: $it" to { fObra = null }) }
        fMes?.let { i -> add("Vence em ${meses[i].first}" to { fMes = null }) }
    }

    PainelDashboard(
        titulo = "Dashboard — Colaboradores (QSMS)",
        subtitulo = "Matriz de documentos em ${DashDatas.br(hojeIso)} · ${FmtDash.qtd(lista.size)} colaborador(es)",
        carregando = carregando,
        onAtualizar = onAtualizar,
        camposCor = listOf(
            "ASO" to 0xFF1E5AA8,
            "Treinamentos (NR)" to 0xFFE07B00,
            "CNH" to 0xFF6A4FB3,
            "Outros" to 0xFF00897B
        ) + StatusDoc.entries.map { it.rotulo to it.cor },
        filtros = {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                listOf("" to "Todos", "CLT" to "CLT", "PJ" to "PJ").forEach { (v, r) -> ChipFiltro(r, vinculo == v) { vinculo = v } }
            }
            FiltrosAtivos(filtrosAtivos) { fStatus = null; fSetor = null; fFuncao = null; fObra = null; fMes = null }
        }
    ) { largo, grande ->
        if (ativos.isEmpty()) {
            SemDadosDash("Sem colaboradores", "Nenhum colaborador ativo na matriz.")
            return@PainelDashboard
        }
        PaineisDoDash("colaboradores", largo, organizar = !grande, itens = listOf(
            ItemPainel("kpis", "Indicadores", inteiro = true, conteudo = {
                GradeKpis(
            largo,
            listOf(
                { m -> KpiDash("Colaboradores ativos", FmtDash.qtd(lista.size), m) },
                { m ->
                    KpiDash(
                        "Conformidade", FmtDash.pct(conformidade), m,
                        cor = when {
                            conformidade == null -> CoresDash.neutro
                            conformidade >= 0.9 -> CoresDash.bom
                            conformidade >= 0.7 -> CoresDash.atencao
                            else -> CoresDash.ruim
                        },
                        legenda = "documentos em dia ÷ exigidos", destaque = true
                    )
                },
                { m -> KpiDash("Vencidos", FmtDash.qtd(vencidos), m, cor = if (vencidos > 0) CoresDash.ruim else CoresDash.bom, destaque = vencidos > 0) },
                { m -> KpiDash("Pendentes", FmtDash.qtd(pendentes), m, cor = if (pendentes > 0) Color(0xFF7B1FA2) else CoresDash.bom, legenda = "obrigatórios sem registro") },
                { m -> KpiDash("Críticos", FmtDash.qtd(criticos), m, cor = PortalCores.Laranja, legenda = "vencem em até ${regras.crit} dias") },
                { m -> KpiDash("Atenção", FmtDash.qtd(atencao), m, cor = CoresDash.atencao, legenda = "vencem em até ${regras.aten} dias") },
                { m -> KpiDash("CNH vencidas", FmtDash.qtd(cnhVencidas), m, cor = if (cnhVencidas > 0) CoresDash.ruim else CoresDash.bom) }
            )
        )
            }),
            ItemPainel("situacao", "Situação dos colaboradores") { m ->
                val porStatus = ativos.filter {
                    (fSetor == null || setor(it) == fSetor) && (fFuncao == null || funcao(it) == fFuncao) && (fObra == null || obra(it) == fObra)
                }
                    .groupingBy { piorDe[it.id] ?: StatusDoc.NA }.eachCount()
                GraficoRosca(
                    "Situação dos colaboradores", "Pior documento de cada um · clique para filtrar",
                    porStatus.entries.sortedByDescending { it.value }.map { (s, n) -> FatiaDash(s.rotulo, n.toDouble(), CoresDash.de(s.rotulo, s.cor)) },
                    m, grande, rotuloTotal = "pessoas", selecionada = fStatus, onClick = { fStatus = alternar(fStatus, it) }
                )
            },
            ItemPainel("conformidade", "Conformidade por documento") { m ->
                BarrasPercentual(
                    "Conformidade por documento", "Em dia ÷ (em dia + vencidos + pendentes)",
                    (DocsMatriz.todos.map { it.rotulo to it.key } + ("Outros" to "OUTRO")).mapNotNull { (rotulo, key) ->
                        val s = if (key == "OUTRO") situacoes.filter { DocsMatriz.porKey(it.second.key) == null }.map { it.third.status }
                        else situacoes.filter { it.second.key == key }.map { it.third.status }
                        val ok = s.count { it.emDia }
                        val v = s.count { it == StatusDoc.VENCIDO }
                        val p = s.count { it == StatusDoc.PENDENTE }
                        if (ok + v + p == 0) null
                        else Triple(rotulo, ok.toDouble() / (ok + v + p), listOfNotNull(v.takeIf { it > 0 }?.let { "$it venc." }, p.takeIf { it > 0 }?.let { "$it pend." }).joinToString(" · "))
                    }.sortedBy { it.second },
                    m
                )
            },
            ItemPainel("vencimentos", "Vencimentos nos próximos 12 meses", inteiro = true) { m ->
                GraficoColunas(
            "Vencimentos nos próximos 12 meses", "Documentos e CNH que vencem a cada mês · clique para ver a lista",
            meses.map { it.first },
            listOf(
                SerieDash("ASO", CoresDash.de("ASO", 0xFF1E5AA8), meses.indices.map { i -> futuros.count { it.doc == "ASO" && mesDe(it) == i }.toDouble() }),
                SerieDash("Treinamentos (NR)", CoresDash.de("Treinamentos (NR)", 0xFFE07B00), meses.indices.map { i -> futuros.count { it.doc != "ASO" && it.doc != "CNH" && it.doc in rotulosMatriz && mesDe(it) == i }.toDouble() }),
                SerieDash("CNH", CoresDash.de("CNH", 0xFF6A4FB3), meses.indices.map { i -> futuros.count { it.doc == "CNH" && mesDe(it) == i }.toDouble() }),
                SerieDash("Outros", CoresDash.de("Outros", 0xFF00897B), meses.indices.map { i -> futuros.count { it.doc !in rotulosMatriz && it.doc != "CNH" && mesDe(it) == i }.toDouble() })
            ),
                    m, grande, fmt = { FmtDash.qtd(it) },
                    selecionada = fMes, onClick = { fMes = if (fMes == it) null else it }
                )
            },
            ItemPainel("lista", "Lista de vencimentos", inteiro = true) {
        val listaVenc = (if (fMes != null) futuros.filter { mesDe(it) == fMes } else vencimentos.filter { it.dias <= regras.aten })
            .sortedBy { it.dias }
        CartaoSecao(
            if (fMes != null) "Vencem em ${meses[fMes!!].first}" else "Vencidos e a vencer em até ${regras.aten} dias",
            subtitulo = "${listaVenc.size} documento(s)"
        ) {
            if (listaVenc.isEmpty()) {
                Text("Nada a vencer neste recorte.", color = PortalCores.CinzaClaro, style = MaterialTheme.typography.bodyMedium)
            } else {
                val ordem = rememberOrdem("NOME")
                val linhas = ordem.aplicar(listaVenc) { v ->
                    when (ordem.col) {
                        "FUNCAO" -> funcao(v.colab)
                        "DOC" -> v.doc
                        "DATA" -> v.data
                        "SIT" -> v.dias.toString().padStart(6, '0')
                        else -> v.colab.nome
                    }
                }
                TabelaPortal(fixar = true, cabecalho = {
                    CelulaCabecalhoOrdenavel("Colaborador", 3f, ordem.marca("NOME")) { ordem.clique("NOME") }
                    if (largo) CelulaCabecalhoOrdenavel("Função", 2f, ordem.marca("FUNCAO")) { ordem.clique("FUNCAO") }
                    CelulaCabecalhoOrdenavel("Documento", 1.4f, ordem.marca("DOC")) { ordem.clique("DOC") }
                    CelulaCabecalhoOrdenavel("Vencimento", 1.4f, ordem.marca("DATA")) { ordem.clique("DATA") }
                    CelulaCabecalhoOrdenavel("Situação", 1.4f, ordem.marca("SIT")) { ordem.clique("SIT") }
                }) {
                    linhas.take(if (grande) 25 else 15).forEach { v ->
                        LinhaTabela {
                            Celula(v.colab.nome, 3f, negrito = true)
                            if (largo) Celula(funcao(v.colab), 2f)
                            Celula(v.doc, 1.4f)
                            Celula(DashDatas.br(v.data), 1.4f)
                            Row(Modifier.weight(1.4f)) {
                                ChipCor(if (v.dias < 0) "há ${-v.dias} d" else if (v.dias == 0) "hoje" else "em ${v.dias} d", Color(v.status.cor))
                            }
                        }
                    }
                }
                if (listaVenc.size > (if (grande) 25 else 15)) {
                    Text("+ ${listaVenc.size - (if (grande) 25 else 15)} outro(s)", style = MaterialTheme.typography.bodySmall, color = PortalCores.CinzaTexto, fontWeight = FontWeight.SemiBold)
                }
            }
        }
            },
            ItemPainel("obra", "Por obra") { m ->
                RankingBarras("Por obra", "Centro de custo do colaborador · clique para filtrar", contagem(lista, ::obra), m, selecionado = fObra, onClick = { fObra = alternar(fObra, it) })
            },
            ItemPainel("setor", "Pendências por setor/GHE") { m ->
                val comPendencia = lista.filter { StatusColab.temPendencia(it, regras, hoje) }
                RankingBarras("Pendências por setor/GHE", "Colaboradores com documento vencido ou pendente · clique para filtrar", contagem(comPendencia, ::setor), m, cor = CoresDash.ruim, selecionado = fSetor, onClick = { fSetor = alternar(fSetor, it) })
            },
            ItemPainel("funcao", "Pendências por função") { m ->
                val comPendencia = lista.filter { StatusColab.temPendencia(it, regras, hoje) }
                RankingBarras("Pendências por função", "Clique para filtrar", contagem(comPendencia, ::funcao), m, cor = PortalCores.Laranja, selecionado = fFuncao, onClick = { fFuncao = alternar(fFuncao, it) })
            }
        ))
    }
}
